import React from 'react'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Button } from './Button'
import { cn } from '../../lib/utils'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  className?: string
  showInfo?: boolean
  totalItems?: number
  itemsPerPage?: number
}

interface PaginationButtonProps {
  page: number | string
  isActive?: boolean
  isDisabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}

const PaginationButton: React.FC<PaginationButtonProps> = ({
  page,
  isActive = false,
  isDisabled = false,
  onClick,
  children
}) => {
  return (
    <Button
      variant={isActive ? 'default' : 'outline'}
      size="sm"
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        'h-8 w-8 p-0',
        isActive && 'pointer-events-none',
        !isActive && !isDisabled && 'hover:bg-accent hover:text-accent-foreground'
      )}
    >
      {children}
    </Button>
  )
}

const generatePaginationRange = (currentPage: number, totalPages: number): (number | 'ellipsis')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', totalPages]
  }

  if (currentPage >= totalPages - 3) {
    return [1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  }

  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages]
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  showInfo = false,
  totalItems = 0,
  itemsPerPage = 20
}) => {
  if (totalPages <= 1) {
    return null
  }

  const paginationRange = generatePaginationRange(currentPage, totalPages)
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {showInfo && (
        <div className="flex-1 text-sm text-muted-foreground">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </div>
      )}
      
      <nav className="flex items-center space-x-1" aria-label="Pagination">
        {/* Previous Button */}
        <PaginationButton
          page="prev"
          isDisabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Previous page</span>
        </PaginationButton>

        {/* Page Numbers */}
        {paginationRange.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <div key={`ellipsis-${index}`} className="flex h-8 w-8 items-center justify-center">
                <MoreHorizontal className="h-4 w-4" />
              </div>
            )
          }

          return (
            <PaginationButton
              key={page}
              page={page}
              isActive={page === currentPage}
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </PaginationButton>
          )
        })}

        {/* Next Button */}
        <PaginationButton
          page="next"
          isDisabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">Next page</span>
        </PaginationButton>
      </nav>
    </div>
  )
}

export default Pagination
